import React, { useState, useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function ElectronProtonCrush() {
  const [pressure, setPressure] = useState<number>(0);

  // Time and animation state trackers
  const anglesRef = useRef<number[]>([0, Math.PI * 0.5, Math.PI, Math.PI * 1.5]);
  const flashAgesRef = useRef<number[]>([0, 0, 0, 0]);
  const fusionStatesRef = useRef<boolean[]>([false, false, false, false]);
  const mergeTimeRef = useRef<number>(0);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2 - 20;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // 1. ATOM GRID AND INITIALIZATION
    const atomGrid = [
      { x: cx - 110, y: cy - 60 },
      { x: cx + 110, y: cy - 60 },
      { x: cx - 110, y: cy + 60 },
      { x: cx + 110, y: cy + 60 }
    ];

    // Determine fusion thresholds
    const thresholds = [90, 92.5, 95, 97.5];
    const isFused = thresholds.map((th) => pressure > th);

    // Check transitions to trigger white flashes
    isFused.forEach((fused, i) => {
      if (fused && !fusionStatesRef.current[i]) {
        flashAgesRef.current[i] = 0.25; // 0.25s flash duration
      }
      fusionStatesRef.current[i] = fused;
    });

    // Update flash ages
    flashAgesRef.current = flashAgesRef.current.map((age) => Math.max(0, age - deltaTime));

    // Calculate neutron count
    const neutronCount = isFused.filter(Boolean).length;
    const allNeutrons = neutronCount === 4;

    // Merge timer
    if (allNeutrons && pressure === 100) {
      mergeTimeRef.current += deltaTime;
    } else {
      mergeTimeRef.current = 0;
    }

    const mergeProgress = Math.min(1.0, mergeTimeRef.current / 1.5); // merge over 1.5s

    // 2. DRAW ATOMS / NEUTRONS
    if (mergeProgress < 1.0) {
      atomGrid.forEach((atom, i) => {
        const fused = isFused[i];
        
        // Dynamic orbital stats
        const orbitalRadius = Math.max(2, 30 - (pressure / 100) * 28);
        const orbitSpeed = 1.2 + pressure / 15;
        
        // Update electron orbit angle
        anglesRef.current[i] += deltaTime * orbitSpeed;
        const angle = anglesRef.current[i];

        // Interpolated nucleus position (drifting to center when all are neutrons)
        const nx = cx + (atom.x - cx) * (1 - mergeProgress);
        const ny = cy + (atom.y - cy) * (1 - mergeProgress);

        // A. Draw Orbit Ring (if not fused yet)
        if (!fused) {
          ctx.strokeStyle = 'rgba(96, 165, 250, 0.25)';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.arc(nx, ny, orbitalRadius, 0, Math.PI * 2);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        // B. Draw Nucleus
        if (fused) {
          // Neutron state (gray circle)
          ctx.fillStyle = '#94A3B8';
          ctx.beginPath();
          ctx.arc(nx, ny, 8, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#1E293B';
          ctx.font = 'bold 9px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('n⁰', nx, ny + 3);
        } else {
          // Proton state (red circle)
          ctx.fillStyle = '#EF4444';
          ctx.beginPath();
          ctx.arc(nx, ny, 8, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#FFFFFF';
          ctx.font = 'bold 9px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('p⁺', nx, ny + 3);
        }

        // C. Draw Electron (if not fused yet)
        if (!fused) {
          const ex = nx + Math.cos(angle) * orbitalRadius;
          const ey = ny + Math.sin(angle) * orbitalRadius;

          ctx.fillStyle = '#60A5FA';
          ctx.beginPath();
          ctx.arc(ex, ey, 4.5, 0, Math.PI * 2);
          ctx.fill();

          // Electron label
          ctx.fillStyle = '#FFFFFF';
          ctx.font = 'bold 6px sans-serif';
          ctx.fillText('e⁻', ex, ey + 2);
        }

        // D. Draw Atom Labels (only when grid is separated)
        if (mergeProgress === 0) {
          ctx.fillStyle = fused ? '#94A3B8' : '#60A5FA';
          ctx.font = 'bold 10px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(fused ? 'NEUTRON' : 'HYDROGEN ATOM', atom.x, atom.y - 42);
        }

        // E. Draw Fusion Flash Frame
        const flashAge = flashAgesRef.current[i];
        if (flashAge > 0) {
          const fGrad = ctx.createRadialGradient(nx, ny, 0, nx, ny, 32);
          fGrad.addColorStop(0, `rgba(255, 255, 255, ${flashAge / 0.25})`);
          fGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
          ctx.fillStyle = fGrad;
          ctx.beginPath();
          ctx.arc(nx, ny, 32, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    }

    // 3. DRAW FULLY MERGED NEUTRON STAR
    if (mergeProgress > 0) {
      const starRadius = 6 + mergeProgress * 18; // grows from 6px to 24px
      const glowOpacity = mergeProgress * 0.8;

      // Outer blue glow
      const sGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, starRadius * 1.8);
      sGlow.addColorStop(0, `rgba(96, 165, 250, ${glowOpacity})`);
      sGlow.addColorStop(0.5, `rgba(96, 165, 250, ${glowOpacity * 0.4})`);
      sGlow.addColorStop(1, 'rgba(96, 165, 250, 0)');
      
      ctx.fillStyle = sGlow;
      ctx.beginPath();
      ctx.arc(cx, cy, starRadius * 1.8, 0, Math.PI * 2);
      ctx.fill();

      // Core (neutron star)
      ctx.fillStyle = '#94A3B8';
      ctx.beginPath();
      ctx.arc(cx, cy, starRadius, 0, Math.PI * 2);
      ctx.fill();

      // Center text
      ctx.fillStyle = '#0F172A';
      ctx.font = `bold ${Math.max(8, Math.round(mergeProgress * 14))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText('n★', cx, cy + (mergeProgress * 5));

      // Neon Star forming label
      ctx.fillStyle = `rgba(34, 197, 94, ${mergeProgress})`;
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('NEUTRON STAR FORMING', cx, cy - 65);
    }

    // Top-Left Headings
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Atomic Collapse & Neutronization', 16, 26);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.font = '10px sans-serif';
    ctx.fillText('Increase pressure to crush electron orbitals into nuclei', 16, 42);

    // Dynamic HUD stats
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`Electrons remaining: ${4 - neutronCount}`, width - 16, 26);
  }, [pressure]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Control overlay */}
      <div className="absolute bottom-3 inset-x-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-1">
        <span className="text-white text-[10px] uppercase font-bold tracking-wider opacity-60 text-center">
          Core Pressure: {pressure.toFixed(0)}%
        </span>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={pressure}
          onChange={(e) => setPressure(parseFloat(e.target.value))}
          className="w-full accent-cyan-400 cursor-pointer h-1.5 bg-gradient-to-r from-purple-500 to-white rounded-lg appearance-none"
        />
      </div>
    </div>
  );
}
